import { EVMAnalyticsEvent } from "./events"
import type { IVMEventDefinition, IVMEventDefinitionErased } from "./types"
import {
  EVMEventCategory,
  EVMEventEntity,
  EVMEventOwner,
  EVMEventStatus,
  EVMFreshnessSla,
  EVMPrivacyClass,
} from "./types"

/**
 * Binds a definition to its event so `requiredProps` typechecks against that event's
 * own payload keys. Without the explicit type argument at the call site TypeScript
 * infers `K` from nothing and the check silently widens — always write
 * `defineEvent<EVMAnalyticsEvent.FOO>({ … })`.
 */
const defineEvent = <K extends EVMAnalyticsEvent>(definition: IVMEventDefinition<K>): IVMEventDefinition<K> =>
  definition

/**
 * The canonical analytics registry: what every event means, who owns it, what it must
 * carry, and where it sits in the migration.
 *
 * `satisfies Record<EVMAnalyticsEvent, …>` is load-bearing — it makes a missing entry a
 * compile error, which is the mechanism that stops the registry drifting from the enum.
 *
 * Convention: `requiredProps` mirrors the non-optional keys of the event's payload
 * interface. If you make a property required here, make it non-optional there too, so
 * the fire site fails to compile rather than failing an audit days later.
 */
export const VM_ANALYTICS_REGISTRY = {
  // ── Auth / Onboarding ──────────────────────────────────────────────────────
  [EVMAnalyticsEvent.SIGN_UP]: defineEvent<EVMAnalyticsEvent.SIGN_UP>({
    description: "A new user account is created.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.AUTH,
    entity: EVMEventEntity.USER,
    status: EVMEventStatus.ACTIVE,
    // Carries `email` and `email_domain` — predates the no-PII rule.
    privacy: EVMPrivacyClass.CONTAINS_USER_CONTENT,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.SIGN_IN]: defineEvent<EVMAnalyticsEvent.SIGN_IN>({
    description: "An existing user authenticates.",
    // Backend emits on the auth callback; the client also emits on the verify route.
    owner: EVMEventOwner.BOTH,
    category: EVMEventCategory.AUTH,
    entity: EVMEventEntity.USER,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.CONTAINS_USER_CONTENT,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  // ── Subscription ───────────────────────────────────────────────────────────
  [EVMAnalyticsEvent.SUBSCRIPTION_STARTED]: defineEvent<EVMAnalyticsEvent.SUBSCRIPTION_STARTED>({
    description: "A team's paid subscription begins (first successful Stripe checkout).",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.SUBSCRIPTION,
    entity: EVMEventEntity.SUBSCRIPTION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.SUBSCRIPTION_CANCELLED]: defineEvent<EVMAnalyticsEvent.SUBSCRIPTION_CANCELLED>({
    description: "A subscription is cancelled, at period end or immediately.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.SUBSCRIPTION,
    entity: EVMEventEntity.SUBSCRIPTION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.WEEKLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.SUBSCRIPTION_RENEWAL]: defineEvent<EVMAnalyticsEvent.SUBSCRIPTION_RENEWAL>({
    description: "A recurring subscription invoice is paid for an existing subscriber.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.SUBSCRIPTION,
    entity: EVMEventEntity.SUBSCRIPTION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  // ── Billing ────────────────────────────────────────────────────────────────
  [EVMAnalyticsEvent.FAILED_PAYMENT]: defineEvent<EVMAnalyticsEvent.FAILED_PAYMENT>({
    description: "A Stripe charge for a subscription or top-up fails.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.BILLING,
    entity: EVMEventEntity.SUBSCRIPTION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.WEEKLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.PURCHASE]: defineEvent<EVMAnalyticsEvent.PURCHASE>({
    description: "Money is successfully collected — first subscription invoice or a credit top-up.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.BILLING,
    entity: EVMEventEntity.SUBSCRIPTION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  // ── Credits ────────────────────────────────────────────────────────────────
  [EVMAnalyticsEvent.ADD_CREDITS]: defineEvent<EVMAnalyticsEvent.ADD_CREDITS>({
    description: "Credits are granted to a team (plan allocation, top-up or manual adjustment).",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREDITS,
    entity: EVMEventEntity.CREDIT,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.CONSUME_CREDITS]: defineEvent<EVMAnalyticsEvent.CONSUME_CREDITS>({
    description: "Credits are debited for a billable operation.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREDITS,
    entity: EVMEventEntity.CREDIT,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: ["type"],
  }),

  // ── Funnel / Conversion ────────────────────────────────────────────────────
  [EVMAnalyticsEvent.LANDING_VIEWED]: defineEvent<EVMAnalyticsEvent.LANDING_VIEWED>({
    description: "An anonymous visitor lands on an ad landing route. Step 1 of the paid funnel (VM-958).",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.FUNNEL,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.PRICING_PAGE_VIEWED]: defineEvent<EVMAnalyticsEvent.PRICING_PAGE_VIEWED>({
    description: "The pricing page is viewed.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.FUNNEL,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.CHECKOUT_STARTED]: defineEvent<EVMAnalyticsEvent.CHECKOUT_STARTED>({
    description: "A user opens Stripe checkout for a plan or top-up.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.FUNNEL,
    entity: EVMEventEntity.SUBSCRIPTION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.CTA_CLICKED]: defineEvent<EVMAnalyticsEvent.CTA_CLICKED>({
    description: "A marketing-site call-to-action is clicked.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.FUNNEL,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: ["cta_location", "cta_text"],
  }),

  // ── UX / Engagement ────────────────────────────────────────────────────────
  [EVMAnalyticsEvent.CLICK_COMING_SOON_FEATURE]: defineEvent<EVMAnalyticsEvent.CLICK_COMING_SOON_FEATURE>({
    description: "A user clicks a feature that is not built yet. Demand signal for the roadmap.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.ENGAGEMENT,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.PRO_FEATURE_UPSELL]: defineEvent<EVMAnalyticsEvent.PRO_FEATURE_UPSELL>({
    description: "A paywall or plan-gate is shown for a feature the current plan excludes.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.ENGAGEMENT,
    entity: EVMEventEntity.USER,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.NOT_ENOUGH_CREDITS_UPSELL]: defineEvent<EVMAnalyticsEvent.NOT_ENOUGH_CREDITS_UPSELL>({
    description: "A user is blocked by an insufficient credit balance and shown the top-up prompt.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.ENGAGEMENT,
    entity: EVMEventEntity.CREDIT,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  // ── Creation & Editing ─────────────────────────────────────────────────────
  [EVMAnalyticsEvent.CREATE_AUTOMATED_SERIES]: defineEvent<EVMAnalyticsEvent.CREATE_AUTOMATED_SERIES>({
    description: "A user sets up a recurring automated content series.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.WEEKLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.TEMPLATE_SELECTED]: defineEvent<EVMAnalyticsEvent.TEMPLATE_SELECTED>({
    description: "A user picks a template to start from.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    // `templateName` is a product string, not user content — safe, but do not add prompts here.
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.TOOL_SELECTED]: defineEvent<EVMAnalyticsEvent.TOOL_SELECTED>({
    description: "A user opens a creation tool from the sidebar or dashboard.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.STORYBOARD_CREATED]: defineEvent<EVMAnalyticsEvent.STORYBOARD_CREATED>({
    description: "A storyboard is generated for a video project.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.STORYBOARD,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.EDITOR_OPENED]: defineEvent<EVMAnalyticsEvent.EDITOR_OPENED>({
    description: "The video editor is opened for a project.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.EDITOR_ASSET_MODIFIED]: defineEvent<EVMAnalyticsEvent.EDITOR_ASSET_MODIFIED>({
    description: "A user modifies an asset on the editor timeline or canvas.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.ASSET,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.VIDEO_RENDER_REQUESTED]: defineEvent<EVMAnalyticsEvent.VIDEO_RENDER_REQUESTED>({
    description: "A render of the composed timeline is requested (preview or final).",
    // Client fires the user-intent path; the API also fires for programmatic renders.
    owner: EVMEventOwner.BOTH,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.VIDEO_RENDER_SUCCEEDED]: defineEvent<EVMAnalyticsEvent.VIDEO_RENDER_SUCCEEDED>({
    description: "A render completes and a playable video file exists.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.VIDEO_RENDER_FAILED]: defineEvent<EVMAnalyticsEvent.VIDEO_RENDER_FAILED>({
    description: "A render fails terminally.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    // `errorCode` must stay a code. Never widen this to a raw provider message.
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [],
  }),

  // ── Asset creation lifecycle (canonical) ───────────────────────────────────
  [EVMAnalyticsEvent.ASSET_CREATE_STARTED]: defineEvent<EVMAnalyticsEvent.ASSET_CREATE_STARTED>({
    description: "A user requests asset creation. Exactly one per top-level request, whatever the child job count.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.ASSET,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [
      "creation_id",
      "surface",
      "output_type",
      "generation_scope",
      "generation_action",
      "transform_type",
      "input_asset_count",
      "requested_asset_count",
    ],
    legacyAliases: ["VIDEO_CREATE_STARTED", "VIDEO_REGENERATED"],
  }),

  [EVMAnalyticsEvent.ASSET_CREATE_COMPLETED]: defineEvent<EVMAnalyticsEvent.ASSET_CREATE_COMPLETED>({
    description: "A creation request delivered at least one asset. Terminal; pairs with STARTED by creation_id.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.ASSET,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [
      "creation_id",
      "surface",
      "output_type",
      "generation_scope",
      "generation_action",
      "transform_type",
      "input_asset_count",
      "requested_asset_count",
      "asset_count",
      "job_count",
      "completion_status",
      "asset_types",
      "latency_ms",
    ],
    legacyAliases: ["VIDEO_ASSETS_GENERATED"],
  }),

  [EVMAnalyticsEvent.ASSET_CREATE_FAILED]: defineEvent<EVMAnalyticsEvent.ASSET_CREATE_FAILED>({
    description: "A creation request delivered zero assets, including timeouts swept by the reaper. Terminal.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.ASSET,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: [
      "creation_id",
      "surface",
      "output_type",
      "generation_scope",
      "generation_action",
      "transform_type",
      "input_asset_count",
      "requested_asset_count",
      "job_count",
      "failure_category",
      "latency_ms",
    ],
  }),

  // ── Distribution & Sharing ─────────────────────────────────────────────────
  [EVMAnalyticsEvent.POST_VIDEO]: defineEvent<EVMAnalyticsEvent.POST_VIDEO>({
    description: "A finished video is published to a connected external platform.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.DISTRIBUTION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.WEEKLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.VIDEO_DOWNLOADED]: defineEvent<EVMAnalyticsEvent.VIDEO_DOWNLOADED>({
    description: "A user downloads a rendered video. The strongest single proxy for delivered value.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.DISTRIBUTION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  // ── Generic tool / API ─────────────────────────────────────────────────────
  [EVMAnalyticsEvent.TOOL_USE]: defineEvent<EVMAnalyticsEvent.TOOL_USE>({
    description: "A generic tool invocation with its model and cost. Operational, not a product funnel step.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.ENGAGEMENT,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: [],
  }),

  // ── Debug ──────────────────────────────────────────────────────────────────
  [EVMAnalyticsEvent.DEBUG_TEST]: defineEvent<EVMAnalyticsEvent.DEBUG_TEST>({
    description: "Synthetic event used to verify the ingestion pipeline end to end.",
    owner: EVMEventOwner.BOTH,
    category: EVMEventCategory.DEBUG,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: ["testId", "source"],
  }),

  // ── AI Script Writer (Bet #117) ────────────────────────────────────────────
  [EVMAnalyticsEvent.SCRIPT_IDEAS_REQUESTED]: defineEvent<EVMAnalyticsEvent.SCRIPT_IDEAS_REQUESTED>({
    description: "A user asks the script writer for ad-angle ideas.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    // Sends only the LENGTH of the product description, never the text. Keep it that way.
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: ["productDescriptionLength", "has_brand_voice", "auth_state"],
  }),

  [EVMAnalyticsEvent.SCRIPT_IDEA_SELECTED]: defineEvent<EVMAnalyticsEvent.SCRIPT_IDEA_SELECTED>({
    description: "A user picks one of the generated script angles.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.DAILY,
    requiredProps: ["angle", "fallback"],
  }),

  [EVMAnalyticsEvent.SCRIPT_IDEA_REGENERATED]: defineEvent<EVMAnalyticsEvent.SCRIPT_IDEA_REGENERATED>({
    description: "A user asks for a fresh set of script angles.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.NONE,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: ["productDescriptionLength"],
  }),

  // ── Generation concurrency funnel (VM-880) ─────────────────────────────────
  // Property names are identical across the three steps on purpose so PostHog joins them.
  [EVMAnalyticsEvent.GENERATION_QUEUE_HIT]: defineEvent<EVMAnalyticsEvent.GENERATION_QUEUE_HIT>({
    description: "A generation is queued rather than run because a concurrency cap was hit.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CONCURRENCY,
    entity: EVMEventEntity.SESSION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: ["tier", "queueReason", "dimension", "label", "current", "limit", "hasUpsell"],
  }),

  [EVMAnalyticsEvent.GENERATION_UPSELL_VIEW]: defineEvent<EVMAnalyticsEvent.GENERATION_UPSELL_VIEW>({
    description: "The concurrency upgrade CTA renders after a queue hit.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CONCURRENCY,
    entity: EVMEventEntity.SESSION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: ["tier", "dimension", "queueReason"],
  }),

  [EVMAnalyticsEvent.GENERATION_UPGRADE_CLICK]: defineEvent<EVMAnalyticsEvent.GENERATION_UPGRADE_CLICK>({
    description: "The concurrency upgrade CTA is clicked.",
    owner: EVMEventOwner.FRONTEND,
    category: EVMEventCategory.CONCURRENCY,
    entity: EVMEventEntity.SESSION,
    status: EVMEventStatus.ACTIVE,
    privacy: EVMPrivacyClass.NO_PII,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: ["tier", "dimension", "queueReason"],
  }),

  // ── Deprecated ─────────────────────────────────────────────────────────────
  // Still ACTIVE at the fire sites; DEPRECATED here means "no new call sites".
  // `analytics-audit` fails on any fire site added after this entry exists.
  [EVMAnalyticsEvent.VIDEO_CREATE_STARTED]: defineEvent<EVMAnalyticsEvent.VIDEO_CREATE_STARTED>({
    description: "Legacy: a video creation request begins. Superseded by ASSET_CREATE_STARTED.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.DEPRECATED,
    replacedBy: EVMAnalyticsEvent.ASSET_CREATE_STARTED,
    deprecatedAt: "2026-07-31",
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.VIDEO_ASSETS_GENERATED]: defineEvent<EVMAnalyticsEvent.VIDEO_ASSETS_GENERATED>({
    description: "Legacy: assets finished generating for a video. Superseded by ASSET_CREATE_COMPLETED.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.ASSET,
    status: EVMEventStatus.DEPRECATED,
    replacedBy: EVMAnalyticsEvent.ASSET_CREATE_COMPLETED,
    deprecatedAt: "2026-07-31",
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.HOURLY,
    requiredProps: [],
  }),

  [EVMAnalyticsEvent.VIDEO_REGENERATED]: defineEvent<EVMAnalyticsEvent.VIDEO_REGENERATED>({
    description:
      "Legacy: a re-generation attempt on an existing video. Folded into ASSET_CREATE_STARTED's generation_action.",
    owner: EVMEventOwner.BACKEND,
    category: EVMEventCategory.CREATION,
    entity: EVMEventEntity.VIDEO,
    status: EVMEventStatus.DEPRECATED,
    replacedBy: EVMAnalyticsEvent.ASSET_CREATE_STARTED,
    deprecatedAt: "2026-07-31",
    privacy: EVMPrivacyClass.PSEUDONYMOUS,
    freshnessSla: EVMFreshnessSla.NONE,
    requiredProps: [],
  }),
} satisfies Record<EVMAnalyticsEvent, IVMEventDefinitionErased>

/** All registered events, in declaration order. */
export const VM_ANALYTICS_EVENT_KEYS = Object.keys(VM_ANALYTICS_REGISTRY) as EVMAnalyticsEvent[]

/** Runtime lookup that keeps the erased shape — for the audit, doc generator and CLI. */
export const getEventDefinition = (event: EVMAnalyticsEvent): IVMEventDefinitionErased => VM_ANALYTICS_REGISTRY[event]
